import React from "react";

import { Box, Text } from "@chakra-ui/react";

import { ReportFragment } from "../../../graphql/generated/definitions";
import { QuestionWithAnswers } from "../types/ReportFormTypes";

import useTranslations from "../../../hooks/useTranslations";
import usePreviousAnswers from "../hooks/usePreviousAnswers";

import { getAnswerValue } from "../utils/AnswerValueHelpers";

type QuestionPrintProps = {
  question: QuestionWithAnswers;
  report: ReportFragment;
};

export const QuestionTextPrint: React.FC<QuestionPrintProps> = ({
  question,
  report,
}) => {
  const t = useTranslations();
  const answers = usePreviousAnswers(question, report, false);

  return (
    <Box className="avoid-break">
      <Box>{t("reports.answers")}</Box>
      {answers.map((answer, index) => (
        <Box key={answer.id}>
          {index > 0 && <hr />}
          <Text>
            {t("reports.shared.answerOf", { name: answer.user.name })}
          </Text>
          <Text>{getAnswerValue(answer)}</Text>
        </Box>
      ))}
    </Box>
  );
};

export const QuestionSelectorSinglePrint: React.FC<QuestionPrintProps> = ({
  question,
  report,
}) => {
  const t = useTranslations();
  const answers = usePreviousAnswers(question, report, false);

  return (
    <Box className="avoid-break">
      <Box pt="2">
        {question.options.map((option, index) => (
          <Box pt="1" key={index}>
            <Box
              w="4"
              h="4"
              rounded="full"
              border="1px solid"
              borderColor="black"
              display="inline-block"
              verticalAlign="center"
            />
            <Box display="inline-block" pl="2" verticalAlign="center">
              {option.label}
            </Box>
          </Box>
        ))}
      </Box>
      <Box pt="2">{t("reports.answers")}</Box>
      {answers.map((answer, index) => (
        <Box pt="2" key={index}>
          <Box display="inline-block">{answer.user.name}:</Box>
          <Box display="inline-block" pl="2">
            {question.options[getAnswerValue(answer)]?.label ||
              "Aucune réponse"}
          </Box>

          {answer?.value?.comment && (
            <Text fontSize="sm">
              {t("reports.comments")}: {answer?.value?.comment}
            </Text>
          )}
        </Box>
      ))}
    </Box>
  );
};

export const QuestionSelectorMultiplePrint: React.FC<QuestionPrintProps> = ({
  question,
  report,
}) => {
  const t = useTranslations();
  const answers = usePreviousAnswers(question, report, false);

  return (
    <Box className="avoid-break">
      <Box pt="2">
        {question.options.map((option, index) => (
          <Box pt="1" key={index}>
            <Box
              w="4"
              h="4"
              rounded="sm"
              border="1px solid"
              borderColor="black"
              display="inline-block"
              verticalAlign="center"
            />
            <Box display="inline-block" pl="2" verticalAlign="center">
              {option.label}
            </Box>
          </Box>
        ))}
      </Box>
      <Box pt="2">{t("reports.answers")}</Box>
      {answers.map((answer, index) => (
        <Box key={index}>
          <Box display="inline-block">{answer.user.name}:</Box>
          <Box display="inline-block" pl="2">
            {(getAnswerValue(answer) as unknown as number[])
              ?.map(
                (selector) =>
                  question.options[selector]?.label || "Aucune réponse"
              )
              .join(", ")}
          </Box>
          {answer?.value?.comment && (
            <Text fontSize="sm">
              {t("reports.comments")}: {answer?.value?.comment}
            </Text>
          )}
        </Box>
      ))}
    </Box>
  );
};
